import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DateTimePicker, LoadingButton, LocalizationProvider } from '@mui/lab';
import AdapterMoment from '@mui/lab/AdapterMoment';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextFieldProps,
  useTheme,
} from '@mui/material';
import moment, { Moment } from 'moment';

import {
  ISectionForAdmin,
  TUpdateCourseSectionAsAdmin,
} from '../../../../../store/V2/SectionSlice';
import { getAuthState } from '../../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/UserSlice';
import { updateCourseSectionAsAdminSavingSlice } from '../../../../../store/V2/SavingSlice';
import { useAppSelector } from '../../../../../hooks';
import { checkUserPermission } from '../../../../../helper';
import { TPTextField } from '../../../../../components/Inputs';

export interface SectionOptionsModalProps {
  open: boolean;
  onClose: () => void;
  section: ISectionForAdmin;
}

type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  startDate: null,
  endDate: null,
};

const SectionOptionsModal: React.FC<SectionOptionsModalProps> = ({
  open,
  onClose,
  section,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setLoading] = useState(false);

  const isSubmitDisabled = !state.startDate && !state.endDate;

  useEffect(() => {
    setState((prevState) => {
      const newState = {
        ...prevState,
      };

      if (section.startDate) {
        newState.startDate = moment(section.startDate);
      }

      if (section.endDate) {
        newState.endDate = moment(section.endDate);
      }

      return newState;
    });
  }, []);

  const handleStartDateTimeChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        startDate: value,
      }));
    }
  };

  const handleEndDateTimeChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        endDate: value,
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    const apiData: TUpdateCourseSectionAsAdmin = {
      _id: section._id,
      course: section.course,
    };

    if (state.startDate) {
      apiData.startDate = state.startDate.clone().toISOString();
    }

    if (state.endDate) {
      apiData.endDate = state.endDate.clone().toISOString();
    }

    await dispatch(updateCourseSectionAsAdminSavingSlice(apiData));

    setLoading(false);

    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { minHeight: '300px' } }}
    >
      <DialogTitle>Edit Options</DialogTitle>
      <DialogContent sx={{ pt: `${theme.spacing(2)} !important` }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  value={state.startDate}
                  minDate={moment()}
                  onChange={handleStartDateTimeChange}
                  inputFormat="YYYY-MM-DD hh:mm A"
                  label="Start Date & Time"
                  InputProps={{ readOnly: true }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Start Date & Time"
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  value={state.endDate}
                  minDate={moment()}
                  onChange={handleEndDateTimeChange}
                  inputFormat="YYYY-MM-DD hh:mm A"
                  label="End Date & Time"
                  InputProps={{ readOnly: true }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="End Date & Time"
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        {checkUserPermission(authState.user, {
          adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
        }) ? (
          <LoadingButton
            size="small"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isSubmitDisabled}
          >
            Save
          </LoadingButton>
        ) : null}
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SectionOptionsModal;
